import React, { useState } from 'react';
import { Collapse } from 'react-collapse';
import { Link as RouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Section, Text14, Text16, Text18, Wrapper } from '../../atoms';
import { Intro } from '../../molecules';

/**
 * Governança corporativa: a stack of collapsible blocks, each holding rich text,
 * any number of downloadable documents and an optional link to another page.
 * Content comes from the governanceSection template, so the number of blocks and
 * documents is whatever the backoffice says it is.
 */
export default function GovernanceSection({ title, blocks, language }) {

  const [open, setOpen] = useState(0);

  const toggle = (index) => setOpen(open === index ? -1 : index);

  if (!blocks || blocks.length === 0) {
    return null;
  }

  /**
   * Editors write absolute urls for external links and a leading slash for pages
   * on this site. Only the second kind should stay inside the router.
   */
  const renderLink = (link) => {
    if (!link || !link.url || !link.title) {
      return null;
    }

    if (link.url.indexOf('/') === 0) {
      return (
        <InternalLink to={`/${language}${link.url}`}>
          <Text16 color="#5BB990" dangerouslySetInnerHTML={{ __html: link.title }} />
        </InternalLink>
      );
    }

    return (
      <ExternalLink href={link.url} target="_blank" rel="noopener noreferrer">
        <Text16 color="#5BB990" dangerouslySetInnerHTML={{ __html: link.title }} />
      </ExternalLink>
    );
  };

  return (
    <Section backgroundColor="#fff">
      <Wrapper both noHeight>
        <Container lg>
          {title &&
            <Header>
              <Intro
                titleSize="H3"
                title={title}
                titleColor="#35383B"
                titleMaxWidth="640px"
              />
            </Header>
          }
          <Blocks>
            {blocks.map((block, index) => {
              const isOpen = open === index;

              return (
                <BlockItem key={String(index)}>
                  <BlockHeader
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => toggle(index)}
                  >
                    <HeaderLeft>
                      {block.icon && block.icon.url &&
                        <Icon src={block.icon.url} alt="" />
                      }
                      <Text18 color="#35383B" font="InterBold" dangerouslySetInnerHTML={{ __html: block.title }} />
                    </HeaderLeft>
                    <Toggle
                      src={isOpen ? '/static/images/minus.svg' : '/static/images/plus.svg'}
                      alt=""
                    />
                  </BlockHeader>

                  <Collapse isOpened={isOpen}>
                    <BlockBody>
                      {block.description &&
                        <Description dangerouslySetInnerHTML={{ __html: block.description }} />
                      }

                      {renderLink(block.link)}

                      {block.documentsIntro &&
                        <Description dangerouslySetInnerHTML={{ __html: block.documentsIntro }} />
                      }

                      {(block.documents && block.documents.length > 0) &&
                        <Documents>
                          {block.documents.map((document, documentIndex) => (
                            (document.file && document.file.url) ?
                              <DocumentLink
                                key={String(documentIndex)}
                                href={document.file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Text14 color="#5BB990" dangerouslySetInnerHTML={{ __html: document.label || document.file.name }} />
                              </DocumentLink>
                              : null
                          ))}
                        </Documents>
                      }
                    </BlockBody>
                  </Collapse>
                </BlockItem>
              );
            })}
          </Blocks>
        </Container>
      </Wrapper>
    </Section>
  );
}

const Header = styled.div`
  margin-bottom: 48px;
`;
const Blocks = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  border-top: 1px solid #D8DADC;
`;
const BlockItem = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-bottom: 1px solid #D8DADC;
  .ReactCollapse--collapse {
    transition: height 500ms;
  }
`;
const BlockHeader = styled.button`
  width: 100%;
  padding: 32px 8px;
  display: flex;
  flex-flow: row;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: 0;
  text-align: left;
  :hover{
    cursor: pointer;
  }
  :focus-visible{
    outline: 2px solid #5BB990;
    outline-offset: -2px;
  }
`;
const HeaderLeft = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 16px;
  padding-right: 16px;
`;
const Icon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex: none;
  @media screen and (max-width: 760px){
    width: 32px;
    height: 32px;
  }
`;
const Toggle = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex: none;
`;
const BlockBody = styled.div`
  box-sizing: border-box;
  background-color: #F9FAFA;
  border-top: 1px solid #D8DADC;
  padding: 24px calc(25px + (40 - 25) * ((100vw - 375px) / (1920 - 375)));
  display: flex;
  flex-flow: column;
  gap: 16px;
`;
const Description = styled.div`
  color: #5D6368;
  font-size: 16px;
  line-height: 26px;
  p{
    margin: 0 0 12px;
  }
  p:last-child{
    margin-bottom: 0;
  }
  ul, ol{
    margin: 0 0 12px;
    padding-left: 20px;
  }
  li{
    margin-bottom: 8px;
  }
  a{
    color: #5BB990;
  }
`;
const Documents = styled.div`
  display: flex;
  flex-flow: column;
  gap: 12px;
`;
/**
 * No leading icon: one would sit in the text flow and push every label 24px
 * right of the body copy above it, which read as an accidental indent. The
 * underline carries the affordance instead, the way links do everywhere else
 * on the site.
 */
const DocumentLink = styled.a`
  display: block;
  /* The underline is painted in the anchor's own colour, not the colour of the
     Text inside it, so without this it comes out in the browser default blue. */
  color: #5BB990;
  text-decoration: underline;
  :hover{
    cursor: pointer;
    opacity: 0.7;
  }
`;
const InternalLink = styled(RouterLink)`
  color: #5BB990;
  text-decoration: underline;
`;
const ExternalLink = styled.a`
  color: #5BB990;
  text-decoration: underline;
`;
